import { decode, encode } from "utils/HelperFunctions";
import { decodeAndValidatePublicLink } from "utils/publicLinkValidation";
import type { PostLoginRoute } from "utils/postLoginRoute";

export const EDIT_CARD_DRAWER = "edit-card";

export type UpdateCardRoute = {
  cardId: string;
  clusterId: string;
  clusterSlug: string;
  loginPath: string;
  postLoginRoute: PostLoginRoute;
};

const PREFIX_PATTERN = /^[A-Za-z][A-Za-z0-9]*_/;
const NUMERIC_ID_PATTERN = /^\d+$/;

// Update-card links are issued outside this app, so besides the usual
// id_cluster_timestamp form also accept one without the timestamp. Either way
// the slug must re-encode to itself, so a tampered value is rejected.
const parseUpdateCardSlug = (id: string) => {
  const publicLink = decodeAndValidatePublicLink(id);
  if (publicLink) {
    return { cardId: publicLink.recordId, clusterId: publicLink.clusterId };
  }

  const prefix = id.match(PREFIX_PATTERN)?.[0];
  if (!prefix) return null;

  try {
    const decodedValue = decode(id);
    const [cardId, clusterId, ...extraParts] = decodedValue.split("_");
    if (
      extraParts.length > 0 ||
      !NUMERIC_ID_PATTERN.test(cardId) ||
      !NUMERIC_ID_PATTERN.test(clusterId) ||
      encode(decodedValue, prefix) !== id
    ) {
      return null;
    }
    return { cardId, clusterId };
  } catch {
    return null;
  }
};

/**
 * The public update-card link no longer shows the card. It sends the customer
 * through login to their Payment Methods page, which opens the edit drawer
 * only when the card is one of their own.
 */
export const getUpdateCardRoute = (
  id: string | null | undefined
): UpdateCardRoute | null => {
  if (!id || id.length > 512) {
    return null;
  }

  // A link built with standard base64 (+ / =) means the same as the
  // unpadded base64url form this app encodes, so compare in that form.
  const parsed = parseUpdateCardSlug(
    id.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "")
  );
  if (!parsed) {
    return null;
  }

  const clusterSlug = encode(parsed.clusterId, "ci_");
  const search = new URLSearchParams({
    drawer: EDIT_CARD_DRAWER,
    cardId: parsed.cardId,
  });

  return {
    ...parsed,
    clusterSlug,
    loginPath: `/${clusterSlug}/login`,
    postLoginRoute: {
      pathname: `/${clusterSlug}/payment-methods`,
      search: `?${search.toString()}`,
      clusterId: parsed.clusterId,
    },
  };
};
